import { SideMenuPage } from '@/features/taxreturn/menus/SideMenuPage';
import { Checkbox } from '@/kit/Checkbox';
import { DataTable, type DataTableColumn } from '@/kit/DataTable';
import { DateField } from '@/kit/DateField';
import { Paginator } from '@/kit/Paginator';
import { Select, SelectOption } from '@/kit/Select';
import { SelectionBar } from '@/kit/SelectionBar';
import type { Dayjs } from 'dayjs';
import { useState } from 'react';
import { notifyError } from '@/core/notify/notify';
import { dateRangeProblem } from '@/core/utils/dates';
import { selectedClient } from '@/core/selectedClient';
import { workspaceService } from '@/core/services/workspace';
import { ExportFormatCode, toIsoDate } from '@/core/models/practice.models';
import type { UserLogQuery, UserLogRow, WorkspaceOption } from '@/core/models/workspace.models';
import { ExportMenu, formatDate, SearchField, useDebouncedDistinct, useLiveState, useOnInit } from './shared';
import './Logs.scss';

interface State {
  dataSource: UserLogRow[];
  selectedIds: ReadonlySet<number>;
  users: WorkspaceOption[];
  actions: WorkspaceOption[];
  /** '' is the "All" option of the user and action selects; null (cleared) means all dates. */
  dateFilter: number | null;
  userFilter: string;
  actionFilter: string;
  searchText: string;
  fromDate: Dayjs | null;
  toDate: Dayjs | null;
  /** Oct-10 #101/#102 (AP Logs 3565/3566): the log is paged as in 3.0 - zero-based page, page size and the total. */
  pageIndex: number;
  pageSize: number;
  totalItemCount: number;
}

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

/** BUG-060: the date filter value of the custom From/To range. */
const CUSTOM = 8;

/**
 * Audit "user log" grid (Angular TaxreturnLogsUserlogsComponent). BUG-059: with a client open it lists only that client's
 * actions; without one it is the firm-wide log.
 */
export function TaxreturnLogsUserlogs() {
  const [s, set, live] = useLiveState<State>({
    dataSource: [],
    selectedIds: new Set(),
    users: [],
    actions: [],
    dateFilter: 0,
    userFilter: '',
    actionFilter: '',
    searchText: '',
    fromDate: null,
    toDate: null,
    pageIndex: 0,
    pageSize: PAGE_SIZE_OPTIONS[0]!,
    totalItemCount: 0,
  });
  const [clientId] = useState(() => selectedClient()?.id ?? null);
  const search = useDebouncedDistinct(300, (text) => {
    set({ searchText: text, pageIndex: 0 });
    getList();
  });

  useOnInit(() => {
    workspaceService.getUserLogOptions().then(
      (options) => set({ users: options.users, actions: options.actions }),
      (error) => notifyError(error),
    );
    getList();
  });

  function query(): UserLogQuery {
    const st = live.current;
    return {
      pageNumber: st.pageIndex + 1,
      pageSize: st.pageSize,
      searchText: st.searchText || null,
      dateFilter: st.dateFilter ?? null,
      userId: st.userFilter || null,
      action: st.actionFilter || null,
      ...(st.dateFilter === CUSTOM ? { fromDate: toIsoDate(st.fromDate), toDate: toIsoDate(st.toDate) } : {}),
      ...(clientId ? { clientId } : {}),
    };
  }

  /** The message for a custom range that runs backwards, else null: such a range is never searched. */
  function rangeProblem(): string | null {
    const { dateFilter, fromDate, toDate } = live.current;
    return dateFilter === CUSTOM ? dateRangeProblem(fromDate, toDate) : null;
  }

  function getList(): void {
    if (rangeProblem()) {
      return;
    }
    workspaceService.getUserLogs(query()).then(
      (response) =>
        set({
          dataSource: response.logList,
          selectedIds: new Set(),
          totalItemCount: response.pagination?.totalItemCount ?? 0,
        }),
      (error) => notifyError(error),
    );
  }

  const onSearch = (text: string) => search((text ?? '').trim());

  function onExport(format: ExportFormatCode): void {
    const problem = rangeProblem();
    if (problem) {
      notifyError(problem);
      return;
    }
    const q = query();
    workspaceService
      .exportUserLogs({
        format,
        ids: [...live.current.selectedIds],
        searchText: q.searchText,
        dateFilter: q.dateFilter,
        fromDate: q.fromDate,
        toDate: q.toDate,
        userId: q.userId,
        action: q.action,
        clientId: q.clientId,
      })
      .then(undefined, (error) => notifyError(error));
  }

  const isAllSelected = () => s.dataSource.length > 0 && s.dataSource.every((row) => s.selectedIds.has(row.id));

  const toggleAll = (checked: boolean) =>
    set((current) => ({ selectedIds: new Set(checked ? current.dataSource.map((row) => row.id) : []) }));

  const toggleRow = (row: UserLogRow, checked: boolean) =>
    set((current) => {
      const selectedIds = new Set(current.selectedIds);
      if (checked) {
        selectedIds.add(row.id);
      } else {
        selectedIds.delete(row.id);
      }
      return { selectedIds };
    });

  const columns: DataTableColumn<UserLogRow>[] = [
    {
      id: 'date',
      header: (
        <>
          <Checkbox checked={isAllSelected()} onChange={(event) => toggleAll(event.target.checked)} />
          Date
        </>
      ),
      cell: (row) => (
        <>
          <Checkbox checked={s.selectedIds.has(row.id)} onChange={(event) => toggleRow(row, event.target.checked)} />
          {formatDate(row.occurredAtUtc, 'DD-MMM-YYYY hh:mm:ss A')}
        </>
      ),
    },
    { id: 'description', header: 'Description', cell: (row) => row.description },
    { id: 'username', header: 'User Name', cell: (row) => row.userName },
    { id: 'action', header: 'Action', cell: (row) => row.action },
  ];

  return (
    <SideMenuPage menu="logs" contentClassName="tr-logs">
      <div className="content-body">
        <div className="content-header mb-0">
          <div className="content-header-left mb-1">
            <SearchField onSearch={onSearch} />
          </div>

          <div className="content-header-right mb-1">
            {/* BUG-060: the custom range only shows its dates once Custom is chosen. */}
            {s.dateFilter === CUSTOM && (
              <>
                <DateField
                  fieldClassName="w-150"
                  placeholder="From"
                  invalid={dateRangeProblem(s.fromDate, s.toDate) !== null}
                  value={s.fromDate}
                  onChange={(value) => {
                    set({ fromDate: value, pageIndex: 0 });
                    getList();
                  }}
                />
                <DateField
                  fieldClassName="w-150"
                  placeholder="To"
                  min={s.fromDate ?? undefined}
                  invalid={dateRangeProblem(s.fromDate, s.toDate) !== null}
                  value={s.toDate}
                  onChange={(value) => {
                    set({ toDate: value, pageIndex: 0 });
                    getList();
                  }}
                />
              </>
            )}
            <Select
              className="w-150"
              value={s.dateFilter}
              onChange={(value) => {
                set({ dateFilter: value as number | null, pageIndex: 0 });
                getList();
              }}
              openOnFocus
              panelClass="myPanelClass"
            >
              <SelectOption value={0}>Last 30 days</SelectOption>
              <SelectOption value={1}>Last Week</SelectOption>
              <SelectOption value={2}>This Month</SelectOption>
              <SelectOption value={3}>Last Month</SelectOption>
              <SelectOption value={4}>This Quarter</SelectOption>
              <SelectOption value={5}>Last Quarter</SelectOption>
              <SelectOption value={6}>This Year</SelectOption>
              <SelectOption value={7}>Last Year</SelectOption>
              <SelectOption value={CUSTOM}>Custom</SelectOption>
            </Select>
            <Select
              className="w-150"
              value={s.userFilter}
              clearable={false}
              onChange={(value) => {
                set({ userFilter: value as string, pageIndex: 0 });
                getList();
              }}
              openOnFocus
              panelClass="myPanelClass"
            >
              <SelectOption value="">User-All</SelectOption>
              {s.users.map((option) => (
                <SelectOption key={option.id} value={option.id}>
                  {option.name}
                </SelectOption>
              ))}
            </Select>
            <Select
              className="w-150"
              value={s.actionFilter}
              clearable={false}
              onChange={(value) => {
                set({ actionFilter: value as string, pageIndex: 0 });
                getList();
              }}
              openOnFocus
              panelClass="myPanelClass"
            >
              <SelectOption value="">Action-All</SelectOption>
              {s.actions.map((option) => (
                <SelectOption key={option.id} value={option.id}>
                  {option.name}
                </SelectOption>
              ))}
            </Select>
            <ExportMenu onExport={onExport} />
          </div>
        </div>
        {s.dateFilter === CUSTOM && dateRangeProblem(s.fromDate, s.toDate) && (
          <p className="text-danger mb-1">{dateRangeProblem(s.fromDate, s.toDate)}</p>
        )}
        {/* Logs are a record, never deleted: the ticked entries can be exported. */}
        <SelectionBar
          count={s.selectedIds.size}
          onExport={() => onExport(ExportFormatCode.Excel)}
          onClear={() => toggleAll(false)}
        />
        <div className="mat-table-wrapper mat-table-wrapper-pagination no-hover">
          <DataTable stickyHeader columns={columns} rows={s.dataSource} rowKey={(row) => row.id} />
        </div>
        {s.totalItemCount > 0 && (
          <Paginator
            length={s.totalItemCount}
            pageIndex={s.pageIndex}
            pageSize={s.pageSize}
            pageSizeOptions={PAGE_SIZE_OPTIONS}
            showFirstLastButtons
            onPage={(event) => {
              set({ pageIndex: event.pageIndex, pageSize: event.pageSize });
              getList();
            }}
          />
        )}
      </div>
    </SideMenuPage>
  );
}
